<script setup lang="ts">
type Props = {
  label?: string
  rows?: string | number
  placeholder?: string
  counter?: string | number
  disabled?: boolean
  loading?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  rows: 2,
  label: '',
  placeholder: '',
  counter: '',
})

const attrs = useAttrs()

const model = defineModel<string>({ default: '' })

const counterContent = computed(() => {
  return `${model.value.length}/${props.counter}`
})

const fieldStyles = computed(() => {
  if (attrs.width) {
    return { width: `${attrs.width}px` }
  }

  return {}
})
</script>

<template>
  <div class="app-textarea">
    <div v-if="label" data-test-id="textarea-label" class="app-textarea__label">
      {{ label }}
    </div>

    <textarea
      v-model="model"
      data-test-id="textarea-field"
      class="app-textarea__field"
      :style="fieldStyles"
      v-bind="$attrs"
      :placeholder="placeholder"
      :aria-disabled="disabled"
      :disabled="disabled"
      :maxlength="counter"
      :rows="rows"
    />

    <div
      v-if="counter"
      data-test-id="textarea-counter"
      class="app-textarea__counter"
    >
      {{ counterContent }}
    </div>

    <div
      v-if="loading"
      data-test-id="textarea-loader"
      class="app-textarea__loader"
      role="progressbar"
    >
      <the-loader variant="linear" />
    </div>
  </div>
</template>

<style scoped lang="scss">
@use '../../assets/ui-layer/styles/mixins' as *;

.app-textarea {
  position: relative;

  &__label {
    margin-bottom: 2px;
    font-size: 12px;
    font-weight: 600;
    line-height: 16px;
    color: var(--ui-text-primary);
  }

  &__counter {
    position: absolute;
    right: 4px;
    bottom: -2px;
    z-index: 1;
    font-size: 12px;
    font-weight: 600;
    line-height: 16px;
    color: var(--ui-text-secondary);
    text-align: right;
    visibility: hidden;
    user-select: none;
    opacity: 0;
    transition: all 0.2s ease-in-out;
  }

  &__field {
    position: relative;
    z-index: 2;
    display: block;
    width: 100%;
    min-height: 58px;
    padding: 12px 12px 10px;
    font-family: inherit;
    font-size: 14px;
    font-weight: 600;
    line-height: 18px;
    color: var(--ui-text-primary);
    resize: none;
    background-color: var(--ui-grey);
    border: none;
    border-radius: 10px;
    outline: none;
    transition: background-color 0.3s ease;

    &:disabled {
      opacity: 0.5;
    }

    &:hover {
      background-color: var(--ui-grey--hover);
    }

    &:focus,
    &:focus-visible {
      & ~ .app-textarea__counter {
        visibility: visible;
        opacity: 1;
        transform: translateY(100%);
      }
    }

    &::placeholder {
      font-size: inherit;
      font-weight: inherit;
      line-height: inherit;
      color: var(--ui-text-secondary);
    }
  }

  &__loader {
    position: absolute;
    right: 0;
    bottom: 0;
    left: 0;
    height: 2px;
  }
}
</style>
